import { supabase } from '@/lib/integrations/supabase'
import type { AccountingClient } from '@/modules/accounting/clients/accountingClientTypes'
import {
  getAccountingDocumentMovement,
  isCfopCompatibleWithMovement,
  taxIdsRepresentSameCompany,
} from '@/modules/accounting/accountingDocumentMovement'
import { getDocumentDownloadUrl, type StoredDocument } from '@/modules/documents'
import {
  createLayoutDefinitionSkeleton,
  definitionFromFileLayout,
  executeFileLayout,
  getStoredDocumentType,
  hasBuiltInFiscalLayout,
  isLayoutCompatibleWithDocument,
  listFileLayouts,
  resolveExecutedFileLayout,
  type DeclarativeFileLayout,
} from '@/modules/file-reader'

const CURRENT_TAXES = ['ipi', 'icms', 'pis', 'cofins'] as const
type CurrentTax = typeof CURRENT_TAXES[number]

interface AssetCodeRow { asset_id: string; party_id: string; code: string }
interface PartyTaxIdRow { party_id: string; tax_id: string }
interface SimilarAssetRow {
  asset_id: string
  ncm: string | null
  asset: { name: string; uom: string | null } | Array<{ name: string; uom: string | null }> | null
}
interface ProductDescriptionRow {
  asset_id: string
  description: string | null
  inventory_uom: string | null
  ncm: string | null
}
interface ProductAlias {
  description: string
  uom: string | null
  ncm: string | null
}
interface ProductCandidate {
  assetId: string
  productScore: number
  basis: 'exact_code' | 'ncm_description' | 'description'
}
interface OperationMemoryRow {
  id: string
  asset_id: string
  party_id: string | null
  source_cfop: string | null
  bookkeeping_cfop: string | null
  item_purpose_key: string | null
  operation_nature: string | null
  counterparty_state: string | null
  counterparty_tax_regime: string | null
  occurrence_count: number
  period_count: number
  confidence: number
}
interface TaxMemoryRow {
  operation_memory_id: string
  tax_code: string
  code_system: string | null
  situation_code: string | null
  nominal_rate: number | null
  base_reduction_rate: number | null
  effective_rate: number | null
  credit_rate: number | null
  has_credit: boolean | null
  occurrence_count: number
  period_count: number
  confidence: number
}

export interface IncomingTaxSuggestion {
  taxCode: CurrentTax
  situationCode: string | null
  codeSystem: string | null
  baseAmount: number | null
  baseReduction: number | null
  rate: number | null
  taxAmount: number | null
  creditRate: number | null
  creditAmount: number | null
  hasCredit: boolean
  confidence: number
  periodCount: number
}

export interface IncomingItemSuggestion {
  lineNumber: number
  description: string
  externalCode: string | null
  sourceCfop: string | null
  bookkeepingCfop: string | null
  itemPurpose: string | null
  operationNature: string | null
  matchKind: 'exact' | 'similar' | 'unresolved'
  productMatchBasis: 'exact_code' | 'ncm_description' | 'description' | 'unresolved'
  confidence: number
  periodCount: number
  taxes: IncomingTaxSuggestion[]
  warning: string | null
}

export interface IncomingBookkeepingPreview {
  document: StoredDocument
  movement: Record<string, unknown>
  items: Array<Record<string, unknown>>
  suggestions: IncomingItemSuggestion[]
  warnings: string[]
  confidence: number
  readerKey: string
  readerVersion: string
  mappingVersion: string
  layoutName: string
}

const valueFrom = (record: Record<string, unknown>, path: string) => {
  let value: unknown = record
  for (const segment of path.split('.')) {
    if (!value || typeof value !== 'object') return null
    value = (value as Record<string, unknown>)[segment]
  }
  return value
}
const stringValue = (value: unknown) => typeof value === 'string' && value.trim() ? value.trim() : null
const numberValue = (value: unknown) => typeof value === 'number' && Number.isFinite(value) ? value : null
const digits = (value: unknown) => String(value || '').replace(/\D/g, '')
const normalizedRegime = (value: unknown) => {
  const crt = stringValue(value)
  if (crt && ['1', '2', '4'].includes(crt)) return 'simples_nacional'
  if (crt === '3') return 'normal'
  return crt
}
const rounded = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100
const normalizedText = (value: unknown) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim()
const normalizedProductCode = (value: unknown) => normalizedText(value).replace(/\s+/g, '')
const productName = (row: SimilarAssetRow) => Array.isArray(row.asset)
  ? row.asset[0]?.name || ''
  : row.asset?.name || ''
const productUom = (row: SimilarAssetRow) => Array.isArray(row.asset)
  ? row.asset[0]?.uom || null
  : row.asset?.uom || null

async function pagedRows<T>(load: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: unknown }>) {
  const result: T[] = []
  const pageSize = 1000
  for (let page = 0; ; page += 1) {
    const { data, error } = await load(page * pageSize, ((page + 1) * pageSize) - 1)
    if (error) throw error
    const current = data || []
    result.push(...current)
    if (current.length < pageSize) return result
  }
}

function productSimilarity(left: unknown, right: unknown) {
  const normalizedLeft = normalizedText(left)
  const normalizedRight = normalizedText(right)
  if (!normalizedLeft || !normalizedRight) return 0
  if (normalizedLeft === normalizedRight) return 100
  if (normalizedLeft.includes(normalizedRight) || normalizedRight.includes(normalizedLeft)) return 85
  const leftTokens = new Set(normalizedLeft.split(' ').filter((token) => token.length > 1))
  const rightTokens = new Set(normalizedRight.split(' ').filter((token) => token.length > 1))
  const intersection = Array.from(leftTokens).filter((token) => rightTokens.has(token)).length
  const union = new Set([...leftTokens, ...rightTokens]).size
  const tokenScore = union > 0 ? Math.round((intersection / union) * 100) : 0
  const grams = (value: string) => {
    const compact = value.replace(/\s+/g, ' ')
    if (compact.length < 3) return new Set([compact])
    return new Set(Array.from({ length: compact.length - 2 }, (_, index) => compact.slice(index, index + 3)))
  }
  const leftGrams = grams(normalizedLeft)
  const rightGrams = grams(normalizedRight)
  const gramIntersection = Array.from(leftGrams).filter((gram) => rightGrams.has(gram)).length
  const gramScore = leftGrams.size + rightGrams.size > 0
    ? Math.round((2 * gramIntersection / (leftGrams.size + rightGrams.size)) * 100)
    : 0
  const leftNumbers = new Set(normalizedLeft.match(/\b\d+(?:[.,]\d+)?\b/g) || [])
  const rightNumbers = new Set(normalizedRight.match(/\b\d+(?:[.,]\d+)?\b/g) || [])
  const conflictingNumbers = leftNumbers.size > 0 && rightNumbers.size > 0
    && !Array.from(leftNumbers).some((number) => rightNumbers.has(number))
  return conflictingNumbers ? Math.min(55, Math.max(tokenScore, gramScore)) : Math.max(tokenScore, gramScore)
}

function productCandidateScore(
  description: unknown,
  uom: unknown,
  ncm: string,
  aliases: ProductAlias[],
): Omit<ProductCandidate, 'assetId'> | null {
  const comparable = aliases.map((alias) => ({
    ...alias,
    descriptionScore: productSimilarity(description, alias.description),
    sameUom: Boolean(stringValue(uom) && stringValue(alias.uom)
      && normalizedText(uom) === normalizedText(alias.uom)),
  }))
  if (comparable.length === 0) return null
  const sameNcm = ncm
    ? comparable.filter((alias) => digits(alias.ncm) === ncm)
    : []
  if (sameNcm.length > 0) {
    const best = sameNcm.sort((left, right) => right.descriptionScore - left.descriptionScore || Number(right.sameUom) - Number(left.sameUom))[0]
    return {
      productScore: Math.min(100, 55 + Math.round(best.descriptionScore * 0.4) + (best.sameUom ? 5 : 0)),
      basis: 'ncm_description',
    }
  }
  const best = comparable.sort((left, right) => right.descriptionScore - left.descriptionScore || Number(right.sameUom) - Number(left.sameUom))[0]
  if (best.descriptionScore < 60) return null
  return {
    productScore: Math.max(0, Math.min(95, best.descriptionScore + (best.sameUom ? 5 : -10))),
    basis: 'description',
  }
}

function memoryScore(
  memory: OperationMemoryRow,
  supplierPartyIds: Set<string>,
  sourceCfop: string | null,
  supplierState: string | null,
  supplierRegime: string | null,
) {
  const exactParty = Boolean(memory.party_id && supplierPartyIds.has(memory.party_id))
  const sameState = Boolean(supplierState && memory.counterparty_state === supplierState)
  const sameRegime = Boolean(supplierRegime && memory.counterparty_tax_regime === supplierRegime)
  const missingState = !supplierState || !memory.counterparty_state
  const missingRegime = !supplierRegime || !memory.counterparty_tax_regime
  if (!exactParty && !(sameState && sameRegime) && !(sameState && missingRegime) && !(sameRegime && missingState)) return -1
  let score = exactParty ? 100 : sameState && sameRegime ? 45 : 25
  if (sourceCfop && memory.source_cfop === sourceCfop) score += 30
  else if (memory.source_cfop) score -= 15
  if (sameState) score += 10
  if (sameRegime) score += 10
  score += Number(memory.confidence || 0) * 20
  return score
}

function memoryContextConfidence(
  memory: OperationMemoryRow,
  supplierPartyIds: Set<string>,
  supplierState: string | null,
  supplierRegime: string | null,
) {
  if (memory.party_id && supplierPartyIds.has(memory.party_id)) return 100
  const sameState = Boolean(supplierState && memory.counterparty_state === supplierState)
  const sameRegime = Boolean(supplierRegime && memory.counterparty_tax_regime === supplierRegime)
  if (sameState && sameRegime) return 85
  if (sameState && (!supplierRegime || !memory.counterparty_tax_regime)) return 70
  if (sameRegime && (!supplierState || !memory.counterparty_state)) return 65
  return 0
}

function taxSuggestion(tax: TaxMemoryRow, totalCost: number | null): IncomingTaxSuggestion {
  const rate = tax.effective_rate ?? tax.nominal_rate
  const reduction = tax.base_reduction_rate
  const hasCredit = Boolean(tax.has_credit && (tax.credit_rate ?? rate ?? 0) > 0)
  const isIcmsWithoutRate = tax.tax_code.toLowerCase() === 'icms' && (tax.credit_rate ?? rate ?? 0) <= 0
  const base = !isIcmsWithoutRate && totalCost !== null && rate !== null
    ? rounded(totalCost * Math.max(0, 1 - ((reduction ?? 0) / 100)))
    : null
  const amount = isIcmsWithoutRate ? 0 : base !== null && rate !== null ? rounded(base * rate / 100) : null
  const creditRate = hasCredit ? (tax.credit_rate ?? rate) : null
  const creditAmount = base !== null && creditRate !== null ? rounded(base * creditRate / 100) : null
  return {
    taxCode: tax.tax_code.toLowerCase() as CurrentTax,
    situationCode: tax.situation_code,
    codeSystem: tax.code_system,
    baseAmount: base,
    baseReduction: reduction,
    rate,
    taxAmount: amount,
    creditRate,
    creditAmount,
    hasCredit,
    confidence: Math.round(Number(tax.confidence || 0) * 100),
    periodCount: Number(tax.period_count || 0),
  }
}

export async function prepareIncomingBookkeeping(
  officeUnitId: string,
  client: AccountingClient,
  document: StoredDocument,
): Promise<IncomingBookkeepingPreview> {
  if (getAccountingDocumentMovement(document, client.cnpj) !== 'incoming') {
    throw new Error('accounting_bookkeeping_incoming_required')
  }
  if (!document.storage_path || document.document_kind !== 'xml') {
    throw new Error('accounting_bookkeeping_source_unavailable')
  }

  const response = await fetch(await getDocumentDownloadUrl(document.storage_path))
  if (!response.ok) throw new Error('accounting_bookkeeping_download_failed')
  const buffer = await response.arrayBuffer()
  const documentType = getStoredDocumentType(document)
  if (documentType !== 'nfe') throw new Error('accounting_bookkeeping_incoming_nfe_required')
  const layouts = await listFileLayouts(officeUnitId)
  const layout = layouts.find((candidate) => {
    const definition = definitionFromFileLayout(candidate)
    return definition.direction !== 'export' && isLayoutCompatibleWithDocument(candidate, document)
  }) || null
  const definition: DeclarativeFileLayout | null = layout
    ? definitionFromFileLayout(layout)
    : hasBuiltInFiscalLayout(documentType)
      ? createLayoutDefinitionSkeleton({ type: documentType })
      : null
  if (!definition) throw new Error('accounting_bookkeeping_layout_required')

  const resolved = resolveExecutedFileLayout(definition, executeFileLayout(buffer, definition))
  const sourceAttrs = resolved.movement.attrs as Record<string, unknown>
  if (!taxIdsRepresentSameCompany(client.cnpj, sourceAttrs.recipient_tax_id)) {
    throw new Error('accounting_bookkeeping_incoming_required')
  }

  const supplierTaxId = digits(sourceAttrs.issuer_tax_id)
  const taxIdType = supplierTaxId.length === 14 ? 'cnpj' : supplierTaxId.length === 11 ? 'cpf' : 'other'
  const supplierTaxRoot = taxIdType === 'cnpj' ? supplierTaxId.slice(0, 8) : supplierTaxId
  let partyTaxIdQuery = supabase
    .from('party_tax_ids')
    .select('party_id, tax_id')
    .eq('unit_id', client.client_unit_id)
    .eq('tax_id_type', taxIdType)
  partyTaxIdQuery = taxIdType === 'cnpj'
    ? partyTaxIdQuery.like('tax_id', `${supplierTaxRoot}%`)
    : partyTaxIdQuery.eq('tax_id', supplierTaxId)
  const { data: partyTaxIds, error: partyError } = await partyTaxIdQuery
  if (partyError) throw partyError
  const supplierParties = (partyTaxIds || []) as PartyTaxIdRow[]
  const exactSupplierParty = supplierParties.find((row) => digits(row.tax_id) === supplierTaxId)
  const partyId = exactSupplierParty?.party_id || supplierParties[0]?.party_id || ''
  const supplierPartyIds = new Set(supplierParties.map((row) => row.party_id))

  const externalCodes = Array.from(new Set(resolved.movementItems
    .map((item) => normalizedProductCode(item.external_code))
    .filter((value): value is string => Boolean(value))))
  let assetCodes: AssetCodeRow[] = []
  if (supplierPartyIds.size > 0 && externalCodes.length > 0) {
    const { data, error } = await supabase
      .from('asset_codes')
      .select('asset_id, party_id, code')
      .eq('unit_id', client.client_unit_id)
      .eq('code_type', 'counterparty')
      .in('party_id', Array.from(supplierPartyIds))
    if (error) throw error
    assetCodes = (data || []) as AssetCodeRow[]
  }
  const assetByCode = new Map<string, string>()
  assetCodes
    .sort((left, right) => Number(right.party_id === partyId) - Number(left.party_id === partyId))
    .forEach((row) => {
      const code = normalizedProductCode(row.code)
      if (externalCodes.includes(code) && !assetByCode.has(code)) assetByCode.set(code, row.asset_id)
    })
  let similarAssets: SimilarAssetRow[] = []
  let historicalDescriptions: ProductDescriptionRow[] = []
  const needsSimilarity = resolved.movementItems.some((item) => !assetByCode.has(normalizedProductCode(item.external_code)))
  if (needsSimilarity) {
    [similarAssets, historicalDescriptions] = await Promise.all([
      pagedRows<SimilarAssetRow>((from, to) => supabase
        .from('asset_accounting')
        .select('asset_id, ncm, asset:assets!asset_accounting_asset_id_fkey(name, uom)')
        .eq('unit_id', client.client_unit_id)
        .range(from, to) as any),
      pagedRows<ProductDescriptionRow>((from, to) => supabase
        .from('asset_accounting_evidence')
        .select('asset_id, description, inventory_uom, ncm')
        .eq('unit_id', client.client_unit_id)
        .in('evidence_kind', ['catalog_classification', 'historical_operation'])
        .not('description', 'is', null)
        .range(from, to) as any),
    ])
  }
  const aliasesByAsset = new Map<string, ProductAlias[]>()
  similarAssets.forEach((row) => aliasesByAsset.set(row.asset_id, [{
    description: productName(row),
    uom: productUom(row),
    ncm: row.ncm,
  }]))
  historicalDescriptions.forEach((row) => {
    if (!row.description || !aliasesByAsset.has(row.asset_id)) return
    const aliases = aliasesByAsset.get(row.asset_id) || []
    const signature = `${normalizedText(row.description)}|${normalizedText(row.inventory_uom)}|${digits(row.ncm)}`
    if (!aliases.some((alias) => `${normalizedText(alias.description)}|${normalizedText(alias.uom)}|${digits(alias.ncm)}` === signature)) {
      aliases.push({ description: row.description, uom: row.inventory_uom, ncm: row.ncm })
    }
    aliasesByAsset.set(row.asset_id, aliases)
  })
  const productCandidates = new Map<number, ProductCandidate[]>()
  resolved.movementItems.forEach((item, index) => {
    const exactAssetId = assetByCode.get(normalizedProductCode(item.external_code))
    if (exactAssetId) {
      productCandidates.set(index, [{ assetId: exactAssetId, productScore: 100, basis: 'exact_code' }])
      return
    }
    const attrs = item.attrs && typeof item.attrs === 'object' ? item.attrs as Record<string, unknown> : {}
    const ncm = digits(attrs.ncm)
    const uom = stringValue(item.uom) || stringValue(attrs.uom)
    const scored = similarAssets
      .map((candidate) => {
        const score = productCandidateScore(item.description, uom, ncm, aliasesByAsset.get(candidate.asset_id) || [])
        return score ? { assetId: candidate.asset_id, ...score } : null
      })
      .filter((candidate): candidate is ProductCandidate => Boolean(candidate))
    const ncmMatches = scored.filter((candidate) => candidate.basis === 'ncm_description')
    const candidates = (ncmMatches.length > 0 ? ncmMatches : scored.filter((candidate) => candidate.basis === 'description'))
      .sort((left, right) => right.productScore - left.productScore)
      .slice(0, 8)
    if (candidates.length > 0) productCandidates.set(index, candidates)
  })
  const assetIds = Array.from(new Set([
    ...assetCodes.map((row) => row.asset_id),
    ...Array.from(productCandidates.values()).flatMap((candidates) => candidates.map((candidate) => candidate.assetId)),
  ]))
  let operationRows: OperationMemoryRow[] = []
  if (assetIds.length > 0) {
    const { data, error } = await supabase
      .from('asset_operation_memory')
      .select('id, asset_id, party_id, source_cfop, bookkeeping_cfop, item_purpose_key, operation_nature, counterparty_state, counterparty_tax_regime, occurrence_count, period_count, confidence')
      .eq('unit_id', client.client_unit_id)
      .eq('movement_direction', 'incoming')
      .in('asset_id', assetIds)
    if (error) throw error
    operationRows = ((data || []) as OperationMemoryRow[]).filter((memory) => (
      memory.bookkeeping_cfop === null
      || isCfopCompatibleWithMovement(memory.bookkeeping_cfop, 'incoming')
    ))
  }

  const supplierState = stringValue(sourceAttrs.issuer_state)?.toUpperCase() || null
  const supplierRegime = normalizedRegime(sourceAttrs.issuer_tax_regime)
  const selectedOperations = new Map<number, OperationMemoryRow>()
  const selectedAssets = new Map<number, string>()
  const selectedAssetConfidence = new Map<number, number>()
  const selectedAssetBasis = new Map<number, ProductCandidate['basis']>()
  const selectedContextConfidence = new Map<number, number>()
  resolved.movementItems.forEach((item, index) => {
    const candidates = productCandidates.get(index) || []
    if (candidates.length === 0) return
    selectedAssets.set(index, candidates[0].assetId)
    selectedAssetConfidence.set(index, candidates[0].productScore)
    selectedAssetBasis.set(index, candidates[0].basis)
    const sourceCfop = stringValue(valueFrom(item, 'attrs.cfop'))
    const ranked = candidates
      .flatMap((candidate) => operationRows
        .filter((memory) => memory.asset_id === candidate.assetId)
        .map((memory) => {
          const operationScore = memoryScore(memory, supplierPartyIds, sourceCfop, supplierState, supplierRegime)
          return {
            memory,
            assetId: candidate.assetId,
            productScore: candidate.productScore,
            basis: candidate.basis,
            contextConfidence: memoryContextConfidence(memory, supplierPartyIds, supplierState, supplierRegime),
            eligible: operationScore >= 0,
            score: operationScore + (candidate.productScore * 0.25),
          }
        }))
      .filter(({ eligible }) => eligible)
      .sort((left, right) => right.score - left.score || Number(right.memory.confidence) - Number(left.memory.confidence))
    if (ranked[0]) {
      selectedOperations.set(index, ranked[0].memory)
      selectedAssets.set(index, ranked[0].assetId)
      selectedAssetConfidence.set(index, ranked[0].productScore)
      selectedAssetBasis.set(index, ranked[0].basis)
      selectedContextConfidence.set(index, ranked[0].contextConfidence)
    }
  })

  const operationIds = Array.from(new Set(Array.from(selectedOperations.values()).map((row) => row.id)))
  let taxRows: TaxMemoryRow[] = []
  if (operationIds.length > 0) {
    const { data, error } = await supabase
      .from('asset_operation_tax_memory')
      .select('operation_memory_id, tax_code, code_system, situation_code, nominal_rate, base_reduction_rate, effective_rate, credit_rate, has_credit, occurrence_count, period_count, confidence')
      .eq('unit_id', client.client_unit_id)
      .eq('evidence_role', 'prior_bookkeeping')
      .in('operation_memory_id', operationIds)
    if (error) throw error
    taxRows = (data || []) as TaxMemoryRow[]
  }

  const warnings = [...resolved.warnings]
  const suggestions: IncomingItemSuggestion[] = []
  const preparedItems = resolved.movementItems.map((item, index) => {
    const attrs = item.attrs && typeof item.attrs === 'object' ? item.attrs as Record<string, unknown> : {}
    const externalCode = stringValue(item.external_code)
    const sourceCfop = stringValue(attrs.cfop)
    const assetId = selectedAssets.get(index) || assetByCode.get(normalizedProductCode(externalCode)) || null
    const operation = selectedOperations.get(index) || null
    const exact = Boolean(
      supplierPartyIds.size > 0
      && assetByCode.get(normalizedProductCode(externalCode)) === assetId
      && operation?.party_id
      && supplierPartyIds.has(operation.party_id),
    )
    const taxes = operation
      ? taxRows
        .filter((tax) => tax.operation_memory_id === operation.id && CURRENT_TAXES.includes(tax.tax_code.toLowerCase() as CurrentTax))
        .sort((left, right) => CURRENT_TAXES.indexOf(left.tax_code.toLowerCase() as CurrentTax) - CURRENT_TAXES.indexOf(right.tax_code.toLowerCase() as CurrentTax))
        .map((tax) => taxSuggestion(tax, numberValue(item.total_cost)))
      : []
    const warning = !assetId
        ? 'product_not_in_memory'
        : !operation
          ? (supplierPartyIds.size === 0 ? 'supplier_not_in_memory' : 'operation_not_in_memory')
          : !operation.bookkeeping_cfop
            ? 'bookkeeping_cfop_missing'
            : null
    if (warning) warnings.push(`${warning}:${index + 1}`)
    const confidences = [
      selectedAssetConfidence.get(index) || 0,
      selectedContextConfidence.get(index) || 0,
      Math.round(Number(operation?.confidence || 0) * 100),
      ...taxes.map((tax) => tax.confidence),
    ].filter((value) => value > 0)
    const confidence = confidences.length > 0 ? Math.min(...confidences) : 0
    suggestions.push({
      lineNumber: Number(item.line_number || index + 1),
      description: String(item.description || externalCode || ''),
      externalCode,
      sourceCfop,
      bookkeepingCfop: operation?.bookkeeping_cfop || null,
      itemPurpose: operation?.item_purpose_key || null,
      operationNature: operation?.operation_nature || null,
      matchKind: operation ? (exact ? 'exact' : 'similar') : 'unresolved',
      productMatchBasis: selectedAssetBasis.get(index) || 'unresolved',
      confidence,
      periodCount: Number(operation?.period_count || 0),
      taxes,
      warning,
    })
    const documentTaxValues = Object.fromEntries(CURRENT_TAXES.map((tax) => [tax, {
      situation: attrs[`cst_${tax}`] ?? null,
      base: attrs[`${tax}_base`] ?? null,
      rate: attrs[`${tax}_rate`] ?? null,
      amount: attrs[`${tax}_amount`] ?? null,
    }]))
    const taxAttrs = Object.fromEntries(taxes.flatMap((tax) => [
      [`cst_${tax.taxCode}`, tax.situationCode],
      [`code_system_${tax.taxCode}`, tax.codeSystem],
      [`${tax.taxCode}_base`, tax.baseAmount],
      [`${tax.taxCode}_base_reduction`, tax.baseReduction],
      [`${tax.taxCode}_rate`, tax.rate],
      [`${tax.taxCode}_amount`, tax.taxAmount],
      [`${tax.taxCode}_credit_base`, tax.hasCredit ? tax.baseAmount : null],
      [`${tax.taxCode}_credit_rate`, tax.creditRate],
      [`${tax.taxCode}_credit_amount`, tax.creditAmount],
    ]))
    return {
      ...item,
      attrs: {
        ...attrs,
        ...taxAttrs,
        cfop: operation?.bookkeeping_cfop || null,
        source_cfop: sourceCfop,
        asset_id: assetId,
        item_purpose_key: operation?.item_purpose_key || null,
        memory_operation_id: operation?.id || null,
        memory_source_party_id: operation?.party_id || null,
        memory_match_kind: operation ? (exact ? 'exact' : 'similar') : 'unresolved',
        memory_product_match_basis: selectedAssetBasis.get(index) || null,
        memory_product_match_confidence: selectedAssetConfidence.get(index) || 0,
        memory_similarity_state: exact ? null : supplierState,
        memory_similarity_tax_regime: exact ? null : supplierRegime,
        memory_confidence: confidence,
        document_tax_values: documentTaxValues,
      },
    }
  })

  if (typeof resolved.movement.movement_date !== 'string' || !resolved.movement.movement_date) warnings.push('missing_required:movement_date')
  if (!stringValue(sourceAttrs.document_number)) warnings.push('missing_required:document_number')
  if (numberValue(sourceAttrs.total_amount) === null) warnings.push('missing_required:total_amount')
  const itemConfidences = suggestions.filter((item) => item.confidence > 0).map((item) => item.confidence)
  const confidence = itemConfidences.length > 0 ? Math.min(...itemConfidences) : 0
  const movement = {
    ...resolved.movement,
    description: resolved.movement.description || `Entrada fiscal NF-e ${String(sourceAttrs.document_number || '')}`.trim(),
    status: 'confirmed',
    attrs: {
      ...sourceAttrs,
      bookkeeping_direction: 'incoming',
      fiscal_document_type: documentType,
      memory_assisted: true,
      memory_confidence: confidence,
    },
  }

  return {
    document,
    movement,
    items: preparedItems,
    suggestions,
    warnings,
    confidence,
    readerKey: definition.key,
    readerVersion: String(definition.schemaVersion),
    mappingVersion: layout?.latest_version?.version ? String(layout.latest_version.version) : definition.layoutVersion,
    layoutName: definition.name,
  }
}

export function hasBlockingIncomingWarnings(warnings: string[]) {
  return warnings.some((warning) => ['supplier_not_in_memory', 'product_not_in_memory', 'operation_not_in_memory', 'bookkeeping_cfop_missing'].some((code) => warning.startsWith(code)) || warning.startsWith('missing_required:'))
}

export async function confirmIncomingBookkeeping(preview: IncomingBookkeepingPreview): Promise<string> {
  const { data, error } = await supabase.rpc('bookkeep_accounting_incoming_document', {
    p_document_id: preview.document.id,
    p_movement: preview.movement,
    p_items: preview.items,
    p_trace: {
      direction: 'incoming',
      reader_key: preview.readerKey,
      reader_version: preview.readerVersion,
      mapping_version: preview.mappingVersion,
      layout_name: preview.layoutName,
      warnings: preview.warnings,
      memory_assisted: true,
      memory_confidence: preview.confidence,
      reviewed_by_accountant: true,
    },
  })
  if (error) throw error
  return String(data)
}
